import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  useCreate,
  useDelete,
  useInvalidate,
  useList,
  useUpdate,
} from '@refinedev/core'
import { useMemo, useState } from 'react'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import { getAsciiName } from '../utils/textUtils'
import { RegionSet } from '../interfaces/regions'
import { adminFacingMessage } from '../utils/adminFacingError'

interface ManageRegionSetsModalProps {
  open: boolean
  onClose: () => void
}

interface Region {
  id: number
  name: string
  ascii_name: string | null
  country: string | null
  region: string | null
  council: string | null
  major: string | null
  suburb: string | null
  additional_names: string[] | null
}

export default function ManageRegionSetsModal({
  open,
  onClose: handleCloseDialog,
}: ManageRegionSetsModalProps) {
  const [editingSet, setEditingSet] = useState<RegionSet | null>(null)
  const [isAddingNew, setIsAddingNew] = useState(false)
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Form state
  const [formName, setFormName] = useState('')
  const [formRegions, setFormRegions] = useState<Region[]>([])
  const [formAdditionalNames, setFormAdditionalNames] = useState('')

  // Filter state for region autocomplete
  const [filterCountry, setFilterCountry] = useState<string>('')
  const [filterRegion, setFilterRegion] = useState<string>('')
  const [filterCouncil, setFilterCouncil] = useState<string>('')
  const [filterMajor, setFilterMajor] = useState<string>('')

  const invalidate = useInvalidate()

  const { mutate: createRegionSet } = useCreate()
  const { mutate: updateRegionSet } = useUpdate()
  const { mutate: deleteRegionSet } = useDelete()

  // Fetch region sets
  const { data: regionSetsData, isLoading: regionSetsLoading } =
    useList<RegionSet>({
      resource: 'region-sets',
      meta: {
        paginate: false,
      },
      sorters: [
        {
          field: 'name',
          order: 'asc',
        },
      ],
    })

  // Fetch all regions for the autocomplete
  const { data: regionOptions } = useList<Region>({
    resource: 'regions',
    meta: {
      paginate: false,
    },
    sorters: [
      {
        field: 'id',
        order: 'asc',
      },
    ],
  })

  const regionSets = regionSetsData?.data || []
  const regions = regionOptions?.data || []

  // unique filter options
  const countryOptions = useMemo(() => {
    const countries = new Set<string>()
    regions.forEach((r) => {
      if (r.country) countries.add(r.country)
    })
    return Array.from(countries).sort()
  }, [regions])

  const regionFilterOptions = useMemo(() => {
    const regionNames = new Set<string>()
    regions.forEach((r) => {
      if (r.region && (!filterCountry || r.country === filterCountry)) {
        regionNames.add(r.region)
      }
    })
    return Array.from(regionNames).sort()
  }, [regions, filterCountry])

  const councilOptions = useMemo(() => {
    const councils = new Set<string>()
    regions.forEach((r) => {
      if (
        r.council &&
        (!filterCountry || r.country === filterCountry) &&
        (!filterRegion || r.region === filterRegion)
      ) {
        councils.add(r.council)
      }
    })
    return Array.from(councils).sort()
  }, [regions, filterCountry, filterRegion])

  const majorOptions = useMemo(() => {
    const majors = new Set<string>()
    regions.forEach((r) => {
      if (
        r.major &&
        (!filterCountry || r.country === filterCountry) &&
        (!filterRegion || r.region === filterRegion) &&
        (!filterCouncil || r.council === filterCouncil)
      ) {
        majors.add(r.major)
      }
    })
    return Array.from(majors).sort()
  }, [regions, filterCountry, filterRegion, filterCouncil])

  // Filter regions based on selected filters
  const filteredRegions = useMemo(() => {
    return regions.filter((r) => {
      if (filterCountry && r.country !== filterCountry) return false
      if (filterRegion && r.region !== filterRegion) return false
      if (filterCouncil && r.council !== filterCouncil) return false
      if (filterMajor && r.major !== filterMajor) return false
      return true
    })
  }, [regions, filterCountry, filterRegion, filterCouncil, filterMajor])

  const resetFilters = () => {
    setFilterCountry('')
    setFilterRegion('')
    setFilterCouncil('')
    setFilterMajor('')
  }

  // Get region objects from IDs
  const getRegionsFromIds = (regionIds: number[]): Region[] => {
    return regionIds
      .map((id) => regions.find((r) => r.id === id))
      .filter(Boolean) as Region[]
  }

  const handleStartAdd = () => {
    setIsAddingNew(true)
    setEditingSet(null)
    setFormName('')
    setFormRegions([])
    setFormAdditionalNames('')
    setError(null)
    resetFilters()
  }

  const handleStartEdit = (set: RegionSet) => {
    setEditingSet(set)
    setIsAddingNew(false)
    setFormName(set.name)
    setFormRegions(getRegionsFromIds(set.region_ids))
    setFormAdditionalNames((set.additional_names ?? []).join(', '))
    setError(null)
    resetFilters()
  }

  const handleCancelEdit = () => {
    setEditingSet(null)
    setIsAddingNew(false)
    setFormName('')
    setFormRegions([])
    setFormAdditionalNames('')
    setError(null)
    resetFilters()
  }

  const handleSave = () => {
    setSaving(true)
    const regionIds = formRegions.map((r) => r.id)

    if (isAddingNew) {
      createRegionSet(
        {
          resource: 'region-sets',
          values: {
            name: formName.trim(),
            region_ids: regionIds,
            additional_names: formAdditionalNames.trim(),
          },
          errorNotification: false,
        },
        {
          onSuccess: () => {
            setSaving(false)
            handleCancelEdit()
            invalidate({
              resource: 'region-sets',
              invalidates: ['all'],
            })
          },
          onError: (error: any) => {
            setSaving(false)
            setError(
              adminFacingMessage(error) ??
                'Error creating location set. Please try again.',
            )
          },
        },
      )
    } else if (editingSet) {
      updateRegionSet(
        {
          resource: 'region-sets',
          id: editingSet.id,
          values: {
            name: formName.trim(),
            region_ids: regionIds,
            additional_names: formAdditionalNames.trim(),
          },
          errorNotification: false,
        },
        {
          onSuccess: () => {
            setSaving(false)
            handleCancelEdit()
            invalidate({
              resource: 'region-sets',
              invalidates: ['all'],
            })
          },
          onError: (error: any) => {
            setSaving(false)
            setError(
              adminFacingMessage(error) ??
                'Error updating location set. Please try again.',
            )
          },
        },
      )
    }
  }

  const handleDelete = (id: number) => {
    setSaving(true)
    deleteRegionSet(
      {
        resource: 'region-sets',
        id,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          setSaving(false)
          setDeleteConfirmId(null)
          setError(null)
          invalidate({
            resource: 'region-sets',
            invalidates: ['all'],
          })
        },
        onError: (error: any) => {
          setSaving(false)
          setError(
            adminFacingMessage(error) ??
              'Error deleting location set. Please try again.',
          )
        },
      },
    )
  }

  const isEditing = isAddingNew || editingSet !== null

  return (
    <>
      <Dialog
        open={open}
        onClose={() => {
          // Don't let Escape/backdrop close the modal mid-save -- the
          // eventual success/failure would otherwise have no surface left
          // to appear on (GH #1224 review follow-up).
          if (saving) return
          handleCloseDialog()
        }}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">Manage Location Sets</Typography>
            {!isEditing && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleStartAdd}
              >
                Add Location Set
              </Button>
            )}
          </Box>
        </DialogTitle>

        <DialogContent>
          {isEditing ? (
            <Box sx={{ py: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                {isAddingNew
                  ? 'Create New Location Set'
                  : `Edit: ${editingSet?.name}`}
              </Typography>

              <TextField
                label="Set Name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
                placeholder="e.g., Greater Wellington, Auckland Metro"
              />

              <TextField
                label="Additional names (comma separated)"
                value={formAdditionalNames}
                onChange={(e) => setFormAdditionalNames(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
                placeholder="e.g., Hutt Valley, Te Awa Kairangi"
              />

              {/* Filter dropdowns for region selection */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Country</InputLabel>
                  <Select
                    value={filterCountry}
                    label="Country"
                    onChange={(e) => {
                      setFilterCountry(e.target.value)
                      setFilterRegion('')
                      setFilterCouncil('')
                      setFilterMajor('')
                    }}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {countryOptions.map((country) => (
                      <MenuItem key={country} value={country}>
                        {country}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{ minWidth: 200 }}
                  disabled={regionFilterOptions.length === 0}
                >
                  <InputLabel>Region</InputLabel>
                  <Select
                    value={filterRegion}
                    label="Region"
                    onChange={(e) => {
                      setFilterRegion(e.target.value)
                      setFilterCouncil('')
                      setFilterMajor('')
                    }}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {regionFilterOptions.map((region) => (
                      <MenuItem key={region} value={region}>
                        {region}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{ minWidth: 200 }}
                  disabled={councilOptions.length === 0}
                >
                  <InputLabel>Council</InputLabel>
                  <Select
                    value={filterCouncil}
                    label="Council"
                    onChange={(e) => {
                      setFilterCouncil(e.target.value)
                      setFilterMajor('')
                    }}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {councilOptions.map((council) => (
                      <MenuItem key={council} value={council}>
                        {council}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{ minWidth: 200 }}
                  disabled={majorOptions.length === 0}
                >
                  <InputLabel>Major (Town/City)</InputLabel>
                  <Select
                    value={filterMajor}
                    label="Major (Town/City)"
                    onChange={(e) => setFilterMajor(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {majorOptions.map((major) => (
                      <MenuItem key={major} value={major}>
                        {major}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {(filterCountry ||
                  filterRegion ||
                  filterCouncil ||
                  filterMajor) && (
                  <Button size="small" onClick={resetFilters}>
                    Clear Filters
                  </Button>
                )}
              </Box>

              {(filterCountry ||
                filterRegion ||
                filterCouncil ||
                filterMajor) && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  {filteredRegions.length} region(s) match filters
                </Typography>
              )}

              <Autocomplete
                multiple
                value={formRegions}
                onChange={(_, value) => setFormRegions(value)}
                options={filteredRegions}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(options, params) => {
                  const inputValue = params.inputValue.toLowerCase().trim()
                  if (!inputValue) return options.slice(0, 50) // Show first 50 when empty

                  const asciiInput = getAsciiName(inputValue)
                  const filtered = options.filter((option) => {
                    // Check main name
                    if (option.ascii_name) {
                      if (
                        option.ascii_name.toLowerCase().includes(asciiInput)
                      ) {
                        return true
                      }
                    } else if (getAsciiName(option.name).includes(asciiInput)) {
                      return true
                    }

                    // Check additional_names if they exist
                    if (
                      option.additional_names &&
                      option.additional_names.length > 0
                    ) {
                      return option.additional_names.some(
                        (additionalName: string) =>
                          getAsciiName(additionalName)
                            .toLowerCase()
                            .includes(asciiInput),
                      )
                    }

                    return false
                  })

                  // Sort by match quality: exact > starts with > contains
                  return filtered.sort((a, b) => {
                    const aName = (a.ascii_name || a.name).toLowerCase()
                    const bName = (b.ascii_name || b.name).toLowerCase()

                    const aExact = aName === asciiInput
                    const bExact = bName === asciiInput
                    if (aExact && !bExact) return -1
                    if (bExact && !aExact) return 1

                    const aStarts = aName.startsWith(asciiInput)
                    const bStarts = bName.startsWith(asciiInput)
                    if (aStarts && !bStarts) return -1
                    if (bStarts && !aStarts) return 1

                    // If same match type, shorter names first (more specific)
                    return aName.length - bName.length
                  })
                }}
                renderOption={(props, option) => {
                  const hierarchy = [
                    option.country,
                    option.region,
                    option.council,
                    option.major,
                    option.suburb,
                  ]
                    .filter(Boolean)
                    .join(' → ')

                  return (
                    <li {...props} key={option.id}>
                      <div>
                        <strong>{option.name}</strong>
                        {hierarchy && (
                          <Typography
                            variant="body2"
                            sx={{ color: 'text.secondary', fontSize: '0.85em' }}
                          >
                            {hierarchy}
                          </Typography>
                        )}
                      </div>
                    </li>
                  )
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Regions in this set"
                    placeholder="Search and select regions..."
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index })
                    return <Chip key={key} label={option.name} {...tagProps} />
                  })
                }
              />

              {formRegions.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {formRegions.length} region(s) selected
                  </Typography>
                </Box>
              )}

              {error && (
                <Alert
                  severity="error"
                  onClose={() => setError(null)}
                  sx={{ mt: 2, whiteSpace: 'pre-line' }}
                >
                  {error}
                </Alert>
              )}

              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleCancelEdit}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={
                    saving || !formName.trim() || formRegions.length === 0
                  }
                >
                  {saving
                    ? 'Saving...'
                    : isAddingNew
                    ? 'Create Set'
                    : 'Save Changes'}
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ py: 1 }}>
              {regionSetsLoading ? (
                <Typography>Loading location sets...</Typography>
              ) : regionSets.length === 0 ? (
                <Alert severity="info">
                  No location sets yet. Click "Add Location Set" to create one.
                </Alert>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        Additional names
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Regions</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: 120 }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...regionSets]
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((set) => {
                        const setRegions = getRegionsFromIds(set.region_ids)
                        return (
                          <TableRow key={set.id}>
                            <TableCell>
                              <Typography fontWeight="medium">
                                {set.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {set.additional_names &&
                                set.additional_names.length > 0
                                  ? set.additional_names.join(', ')
                                  : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 0.5,
                                }}
                              >
                                {setRegions.slice(0, 5).map((r) => {
                                  const hierarchy = [
                                    r.country && `Country: ${r.country}`,
                                    r.region && `Region: ${r.region}`,
                                    r.council && `Council: ${r.council}`,
                                    r.major && `Town/City: ${r.major}`,
                                    r.suburb && `Suburb: ${r.suburb}`,
                                  ].filter(Boolean)

                                  return (
                                    <Tooltip
                                      key={r.id}
                                      title={
                                        hierarchy.length > 0 ? (
                                          <Box>
                                            {hierarchy.map((item, idx) => (
                                              <div key={idx}>{item}</div>
                                            ))}
                                          </Box>
                                        ) : (
                                          'No hierarchy details'
                                        )
                                      }
                                      arrow
                                    >
                                      <Chip label={r.name} size="small" />
                                    </Tooltip>
                                  )
                                })}
                                {setRegions.length > 5 && (
                                  <Tooltip
                                    title={
                                      <Box>
                                        {setRegions.slice(5).map((r) => (
                                          <div key={r.id}>{r.name}</div>
                                        ))}
                                      </Box>
                                    }
                                    arrow
                                  >
                                    <Chip
                                      label={`+${setRegions.length - 5} more`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  </Tooltip>
                                )}
                                {setRegions.length === 0 && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    No regions (IDs: {set.region_ids.join(', ')}
                                    )
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => handleStartEdit(set)}
                                title="Edit"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  // Clear any stale error from a previous
                                  // create/update/delete -- otherwise it
                                  // reappears immediately in this unrelated
                                  // dialog (GH #1224 review follow-up).
                                  setError(null)
                                  setDeleteConfirmId(set.id)
                                }}
                                title="Delete"
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                  </TableBody>
                </Table>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmId !== null}
        onClose={() => {
          if (saving) return
          setDeleteConfirmId(null)
          setError(null)
        }}
      >
        <DialogTitle>Delete Location Set?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this location set? This action
            cannot be undone.
          </Typography>
          {error && (
            <Alert
              severity="error"
              onClose={() => setError(null)}
              sx={{ mt: 2, whiteSpace: 'pre-line' }}
            >
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteConfirmId(null)
              setError(null)
            }}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
            color="error"
            variant="contained"
            disabled={saving}
          >
            {saving ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
